"use client";

import type { FC, ReactNode } from "react";
import { cn } from "@/lib/cn";

type SwitchOption = {
  value: string;
  label: string;
  icon?: ReactNode;
};

type SwitcherProps = {
  options: SwitchOption[];
  value: string;
  onValueChange?: (value: string) => void;
  /** `w-full` for the directory's filter panel, where the control spans the field width. */
  className?: string;
};

/**
 * The segmented control. Figma 934:65605 — the *Session Types* switch.
 *
 * Two notes on the chrome:
 *
 * - **The track carries the outer border and each segment past the first carries a left one**, so
 *   every divider is drawn exactly once and the selected segment ends up boxed without any segment
 *   needing to know what its neighbour is doing.
 * - **`rounded-medium` is 18 px against the frame's 16.** The project token wins, as everywhere
 *   else ([public-director.md §9.2](../../../../spec/public-director.md)).
 */
export const Switcher: FC<SwitcherProps> = ({ options, value, onValueChange, className }) => (
  <div
    className={cn(
      "inline-flex items-center overflow-hidden rounded-medium border border-neutral-mid-grey",
      className,
    )}
  >
    {options.map((option, index) => {
      const isActive = option.value === value;

      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={isActive}
          onClick={() => onValueChange?.(option.value)}
          className={cn(
            "flex h-62 min-w-0 flex-1 cursor-pointer items-center justify-center gap-8 py-12 text-16 leading-[1.4] whitespace-nowrap transition-colors",
            /* The frame gives a label-only segment more room than one carrying a glyph. */
            option.icon ? "px-16" : "px-24",
            index > 0 && "border-l border-neutral-mid-grey",
            isActive ? "bg-tone-aqua-tint text-neutral-black" : "bg-neutral-white text-neutral-black/70",
          )}
        >
          {option.icon}
          {option.label}
        </button>
      );
    })}
  </div>
);
